// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { ArrowUpToLine, MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { HardwareValues } from "@/features/stores/store-hardware-tab";
import { EMPTY_WIFI_FALLBACK, type WifiAuthOption, WifiSettingsFields } from "@/features/stores/wifi-settings-fields";

interface WifiFallbacksListProps {
  authOptions: WifiAuthOption[];
  canEdit: boolean;
}

export function WifiFallbacksList({ authOptions, canEdit }: WifiFallbacksListProps) {
  const { t } = useTranslation("back-office");
  const { control, getValues, setValue } = useFormContext<HardwareValues>();
  const { fields, append, remove } = useFieldArray({ control, name: "fieldSettings.wifiFallbacks" });

  // Promoting moves the fallback out of the list into the primary slot, replacing the old primary.
  // The timeout stays with the primary slot, since fallbacks don't carry one.
  function promote(index: number) {
    const fallback = getValues(`fieldSettings.wifiFallbacks.${index}`);
    const { timeout } = getValues("fieldSettings.wifiSettings");
    setValue("fieldSettings.wifiSettings", { ...fallback, timeout }, { shouldDirty: true, shouldTouch: true });
    remove(index);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="text-sm font-medium">{t("stores.hardware.fallbacks.title")}</h4>
          <p className="text-sm text-muted-foreground">{t("stores.hardware.fallbacks.description")}</p>
        </div>
        {canEdit && (
          <Button type="button" variant="outline" size="sm" onClick={() => append({ ...EMPTY_WIFI_FALLBACK })}>
            <Plus className="h-4 w-4" />
            {t("stores.hardware.fallbacks.add")}
          </Button>
        )}
      </div>

      {fields.length === 0 ? (
        <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
          {t("stores.hardware.fallbacks.empty")}
        </p>
      ) : (
        fields.map((item, index) => (
          <WifiFallbackCard
            key={item.id}
            index={index}
            authOptions={authOptions}
            canEdit={canEdit}
            onPromote={() => promote(index)}
            onRemove={() => remove(index)}
          />
        ))
      )}
    </div>
  );
}

interface WifiFallbackCardProps {
  index: number;
  authOptions: WifiAuthOption[];
  canEdit: boolean;
  onPromote: () => void;
  onRemove: () => void;
}

function WifiFallbackCard({ index, authOptions, canEdit, onPromote, onRemove }: WifiFallbackCardProps) {
  const { t } = useTranslation("back-office");
  const { control } = useFormContext<HardwareValues>();
  const path = `fieldSettings.wifiFallbacks.${index}` as const;
  const ssid = useWatch({ control, name: `${path}.ssid` });

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <div className="flex h-8 items-center justify-between gap-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <span className="text-sm font-medium">{t("stores.hardware.fallbacks.itemTitle", { number: index + 1 })}</span>
          {ssid && <span className="truncate text-sm text-muted-foreground">{ssid}</span>}
        </div>
        {canEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label={t("stores.hardware.fallbacks.actions")}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={-32} className="dropdown-overlap">
              <DropdownMenuItem onSelect={onPromote}>
                <ArrowUpToLine className="h-4 w-4" />
                {t("stores.hardware.fallbacks.promote")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={onRemove}>
                <Trash2 className="h-4 w-4" />
                {t("stores.hardware.fallbacks.remove")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <WifiSettingsFields path={path} authOptions={authOptions} />
    </div>
  );
}
